import { LegPlacement, Placement } from "./types/type_def";

/**
 * Leg rule engine: which default legs a module gets, per series and/or module category.
 * Values in the placements are in cm (positionX, positionZ, offsetX, offsetZ) and degrees (rotation).
 */

export type LegRule = { series?: number, categories?: string[] };
export type LegPlacementRule = LegRule & { placements: LegPlacement[] };

/** Used when no rule matches: one leg in each corner */
export const DefaultLegPlacements: LegPlacement[] = [
    { legType: "lt01", placement: Placement.RIGHT_FRONT },
    { legType: "lt01", placement: Placement.RIGHT_BACK },
    { legType: "lt01", placement: Placement.LEFT_FRONT },
    { legType: "lt01", placement: Placement.LEFT_BACK },
];

/**
 * Default leg placements per series and/or category. The most specific matching rule is used:
 * series + categories, then categories only, then series only (on a tie the first one in the list).
 * Example: { series: 148882, categories: ["SEATFRAME"], placements: [{ legType: "lt01", placement: Placement.MIDDLE_LEFT }] }
 */
export const LegPlacementRules: LegPlacementRule[] = [
    { series: 148882, categories: ["SEATFRAME", "BACK"],
        placements: [{ legType: "lt01", placement: Placement.RIGHT_FRONT, offsetZ: -20, rotation: -90 },
    { legType: "lt01", placement: Placement.RIGHT_BACK, offsetZ: 20, rotation: 90 },
    { legType: "lt01", placement: Placement.LEFT_FRONT, offsetZ: -20, rotation: 90 },
    { legType: "lt01", placement: Placement.LEFT_BACK, offsetZ: 20, rotation: -90 }] }


];

/* Seam legs: connection legs (lt02) where two modules meet */

/** cm: a module's own leg standing at most this far past the end of a seam is replaced by the seam leg; further out it stays */
export const SeamCornerLegThreshold: number = 10;

/** Modules whose seams use their full side (whole edge) instead of the connector's side.
    A rule with series and categories matches only those categories in that series; with only series, the whole series;
    with only categories, those categories in every series. Example: { series: 148881, categories: ["CHAISELONG_LEFT", "CHAISELONG_RIGHT"] } */
export const SeamFullSideRules: LegRule[] = [
    { series: 148882, categories: ["SEATFRAME","ARM"] },
    { series: 157546, categories: ["SEATFRAME","ARM"]  },
    { series: 157547, categories: ["SEATFRAME","ARM"]  }
];

/** How many seam legs (cm). A seam no longer than minSpacing gets one leg in the middle (two would stand too close);
    otherwise one at each end, plus legs spread evenly between them so no gap is larger than maxSpacing.
    Both are optional. Rules match like SeamFullSideRules; when several rules match (also from the connected module),
    the largest minSpacing and the smallest maxSpacing apply.
    Example: { series: 148882, categories: ["SEATFRAME"], minSpacing: 50, maxSpacing: 100 } */
export const SeamLegSpacingRules: (LegRule & { minSpacing?: number, maxSpacing?: number })[] = [
    { series: 148882, categories: ["BACK"], minSpacing: 50 },
    { series: 157546, categories: ["BACK"], minSpacing: 50 },
    { series: 157547, categories: ["BACK"], minSpacing: 50 },
    { series: 148882, categories: ["ARM"], maxSpacing: 100 },
    { series: 157546, categories: ["ARM"], maxSpacing: 100 },
    { series: 157547, categories: ["ARM"], maxSpacing: 100 }
];

/** Modules that stand against each other without being connected get seam legs where their sides line up,
    when either module matches a rule (series and/or categories). Example: { series: 148882, categories: ["SEATFRAME"] } */
export const AlignedSeamRules: LegRule[] = [
    { series: 148882, categories: ["BACK"] },
    { series: 157546, categories: ["BACK"] },
    { series: 157547, categories: ["BACK"] },
];

/** cm: how far apart two facing sides of not-connected modules may be and still count as aligned */
export const AlignedSeamMaxGap: number = 2;

/** Sides on which a module keeps its own legs when connected (they are not removed at the seam).
    Rules match like SeamFullSideRules (series and/or categories). Example: { series: 148882, categories: ["BACK"], sides: ["left", "right"] } */
export const KeepLegsOnSeamSideRules: (LegRule & { sides: ("left" | "right" | "front" | "back")[] })[] = [
    { series: 148882, categories: ["SEATFRAME","BACK"], sides: ["left","right"] },
    { series: 157546, categories: ["SEATFRAME","BACK"], sides: ["left","right"] },
    { series: 157547, categories: ["SEATFRAME","BACK"], sides: ["left","right"] }
];

export const IntegratedLegRules: LegRule[] = [
    { series: 1933 }
]

/** A leg rule applies when its series (if given) and categories (if given) match */
export function legRuleMatches(rule: LegRule, series: number | undefined, category: string): boolean {
    return (rule.series === undefined || rule.series === series) && (rule.categories === undefined || rule.categories.includes(category));
}

/** The default leg placements for a module of this series and category */
export function getLegPlacements(series: number | undefined, category: string): LegPlacement[] {
    const specificity = (rule: LegRule) => (rule.series !== undefined && rule.categories !== undefined) ? 3 : rule.categories !== undefined ? 2 : 1;

    let best: LegPlacementRule | undefined;
    for (const rule of LegPlacementRules) {
        if (legRuleMatches(rule, series, category) && (!best || specificity(rule) > specificity(best))) {
            best = rule;
        }
    }
    return best ? best.placements : DefaultLegPlacements;
}

/** TEMP RULE!!!!! Should come from perfion */
export function HasIntegratedLegs(series: number): boolean {
    for (const rule of IntegratedLegRules) {
        if (legRuleMatches(rule, series, undefined)) {
            return true;
        }
    }
    return false
}
